import { createAuthClient } from 'better-auth/react';
import { isPublicDemoRoute } from './public-demo-routes';

/**
 * Custom fetch wrapper that short-circuits the BetterAuth session ping
 * when the user is on a public demo route. Returns an empty 200 JSON body
 * (`{}`) so BetterAuth's client treats the response as "no session"
 * without ever hitting the network.
 *
 * Why: on dev machines where the API isn't running (or port 5000 is taken
 * by macOS Control Center / AirPlay Receiver), the get-session fetch
 * triggers 6 CORS errors per page load. PRD-1 AC1 fails. See
 * `src/lib/public-demo-routes.ts` for the full rationale.
 *
 * This wrapper is a no-op on every non-public route — auth there flows
 * through the real BetterAuth endpoints exactly as before.
 *
 * Gap-close #3 (P0) — PRD-1.
 */
const publicDemoAwareFetch: typeof fetch = (input, init) => {
  if (isPublicDemoRoute()) {
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;
    if (
      url.includes('/auth/get-session') ||
      url.includes('/auth/session') ||
      url.includes('/companies/current-user')
    ) {
      return Promise.resolve(
        new Response('null', {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        }),
      );
    }
  }
  return fetch(input, init);
};

export const authClient = createAuthClient({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000',
  fetchOptions: {
    customFetchImpl: publicDemoAwareFetch,
  },
});
